import { MediaType } from '@pubstack/common/src/media-types'
import { FormattedBidParams, PbsStackMappingOutput, PbsStackOutput } from '@pubstack/common/src/stack'
import { isEqual } from 'lodash-es'
import { useMemo } from 'react'

export const uniqueBidders = (adUnits: PbsStackMappingOutput = {}) =>
  Array.from(
    new Set(
      Object.values(adUnits).reduce((acc: string[], v) => {
        v.forEach((e) => {
          acc.push(e.bidderName)
        })
        return acc
      }, [])
    )
  )

export const adUnitsToBidders = (adUnits: PbsStackMappingOutput = {}) =>
  Object.entries(adUnits).reduce(
    (acc, [k, v]) => {
      v.forEach((e) => {
        acc[e.bidderName] ||= {}

        acc[e.bidderName][k] = { mediaTypes: e.mediaTypes, params: e.params }
      })
      return acc
    },
    {} as Record<string, Record<string, { mediaTypes: MediaType[]; params: FormattedBidParams }>>
  )

export const useBiddersPbsStackChangelog = (output1: PbsStackOutput | undefined, output2: PbsStackOutput | undefined) => {
  const changelog = useMemo(() => {
    const added: Record<string, { adUnitNames?: string[]; bidAdjustment?: number }> = {}
    const removed: Record<string, { adUnitNames?: string[]; bidAdjustment?: number }> = {}
    const changed: Record<string, { adUnitNames?: string[]; bidAdjustment?: number }> = {}

    if (!output1 || !output2) return { changed, added, removed }

    const previousStackBidders = adUnitsToBidders(output1.adUnits) || {}
    const previousBidAdjustments = output1.bidAdjustments || {}
    const currentStackBidders = adUnitsToBidders(output2.adUnits) || {}
    const currentBidAdjustments = output2.bidAdjustments || {}

    // Get all bidders from both versions
    const allBidders = new Set([...Object.keys(currentStackBidders), ...Object.keys(previousStackBidders), ...Object.keys(currentBidAdjustments), ...Object.keys(previousBidAdjustments)])

    allBidders.forEach((bidderName) => {
      const currentAdUnits = Object.keys(currentStackBidders[bidderName] || {})
      const previousAdUnits = Object.keys(previousStackBidders[bidderName] || {})
      const currentBidAdjustment = currentBidAdjustments[bidderName]
      const previousBidAdjustment = previousBidAdjustments[bidderName]

      // Check for added ad units or bid adjustment
      const addedAdUnits = currentAdUnits.filter((unit) => !previousAdUnits.includes(unit))
      const hasBidAdjustmentAdded = currentBidAdjustment !== undefined && previousBidAdjustment === undefined

      if (addedAdUnits.length > 0 || hasBidAdjustmentAdded) {
        added[bidderName] = {
          adUnitNames: addedAdUnits.length > 0 ? addedAdUnits : undefined,
          bidAdjustment: hasBidAdjustmentAdded ? currentBidAdjustment : undefined,
        }
      }

      // Check for removed ad units or bid adjustment
      const removedAdUnits = previousAdUnits.filter((unit) => !currentAdUnits.includes(unit))
      const hasBidAdjustmentRemoved = previousBidAdjustment !== undefined && currentBidAdjustment === undefined

      if (removedAdUnits.length > 0 || hasBidAdjustmentRemoved) {
        removed[bidderName] = {
          adUnitNames: removedAdUnits.length > 0 ? removedAdUnits : undefined,
          bidAdjustment: hasBidAdjustmentRemoved ? previousBidAdjustment : undefined,
        }
      }

      // Check for changed bid adjustment or params
      const bidAdjustmentChanged = currentBidAdjustment !== previousBidAdjustment && currentBidAdjustment !== undefined && previousBidAdjustment !== undefined

      /* Compares the whole mapping, not just its `params`.
         Reading `.params` alone meant a mapping that changed media types — banner to video, with the params
         untouched — produced no chip at all, so the Synchronize dialog listed nothing while the row it was
         opened from was flagged out of sync and its hover popover named the difference. Both sides now ask
         the same question the backend asks when it sets `isOutOfSync`: are the objects equal. */
      const commonAdUnits = currentAdUnits.filter((unit) => previousAdUnits.includes(unit))
      const commonAdUnitsChanged = commonAdUnits.filter((unit) => !isEqual(currentStackBidders[bidderName]?.[unit], previousStackBidders[bidderName]?.[unit]))

      if (commonAdUnitsChanged.length > 0 || bidAdjustmentChanged) {
        changed[bidderName] = {
          adUnitNames: commonAdUnitsChanged.length > 0 ? commonAdUnitsChanged : undefined,
          bidAdjustment: bidAdjustmentChanged ? currentBidAdjustment : undefined,
        }
      }
    })

    return { changed, added, removed }
  }, [output1, output2])

  return { changelog }
}
